import { useState, type ReactNode } from 'react';
import { useT } from '@hana/i18n';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { IconArrowLeft, IconCheck, IconChecklist, IconChevronDown, IconChevronUp, IconClock, IconCopy, IconDots, IconFileText, IconInfoCircle, IconLink, IconListDetails, IconNotes, IconShieldCheck, IconUsers, IconGitBranch, IconHistory } from '@tabler/icons-react';
import { DataError, useList } from '@hana/data';
import { Page, PageHeader, useLayout } from '@hana/shell';
import { Button, Notes, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { NOW_MIN, RELEASE_STEPS } from './fixtures';
import { finalLeft, isBlocked } from './logic';
import { view, type BlRec } from './BlDrawer';
import { PAD_DRAFT_X } from './compact';
import type { BlDraft } from './types';
import s from './blDetail.module.css';

// Mint-BL · trang chi tiết 2:1 (DP21, QĐ 25/Oct 07:05) cho Draft · Final · Release. Chỉ đọc; nút ghi báo toast.
// Release: thẻ "Công nợ khách" là tiền → ẩn (money = hide). Sửa sau PH · LOI · Gốc · Manifest dùng chung khung (phí AMD là tiền → ẩn).

const TIP = {
  sum: 'Trường chính của bản ghi · nguồn TblBL / TblJob · Mail theo mẫu [BL Draft] kèm link myHANA để khách bấm Xác nhận hoặc Yêu cầu sửa. Gửi ngoài hệ thống sẽ bị HANA Box nhắc.',
  type: 'Đổi loại sau khi đã gửi Draft → Draft cũ hết hiệu lực, ghi lịch sử',
  loi: 'LOI theo mẫu của hãng · tự điền dữ liệu job ra PDF, khách ký đóng dấu',
  ver: 'Mỗi vòng sửa là một phiên bản · bản cũ hết hiệu lực khi gửi bản mới',
  fin: 'ATD + 2 giờ làm việc (WorkCalendar) · cờ FinalBL chỉ bật khi mail gửi thành công, không bật khi chỉ tải file lên',
  chain: 'Bước duyệt theo ghế + thẩm quyền (TblOrgTitle.AuthorityCodes) · cấp duyệt theo mức nợ',
  todo: 'Việc chứng từ còn mở của bản ghi · tối đa 4 việc',
  info: 'Mã và trường chính · bấm biểu tượng để sao chép',
  pic: 'Hai nguồn, gộp và bỏ trùng: (1) theo khách: Sale, CS, Sale Asst, Kế toán ở hồ sơ khách (CUSDATA), job chép sang dòng booking cùng người Add PIC; (2) nhận xử lý / được giao: người thực hiện trên chính bản ghi theo mô tả công việc (TblOrgJD). Chưa có người hiện “Chưa giao”; nút Giao chỉ hiện khi có quyền. Avatar theo TblStaff.ImageProfile.',
  links: 'Mở bản ghi liên quan trong HANA Ezy',
} as const;

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

const Info = ({ tip }: { tip: string }) => (
  <span className={s.tip} role="img" aria-label={`Giải thích: ${tip}`} title={tip}>
    <IconInfoCircle size={14} stroke={1.9} aria-hidden="true" />
  </span>
);

function Card({ id, title, icon, tip, pills, actions, children }: { id: string; title: string; icon: ReactNode; tip: string; pills?: ReactNode; actions?: ReactNode; children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <section aria-label={title} className={s.card}>
      <div className={s.ch}>
        <span className={s.chIco} aria-hidden="true">{icon}</span>
        <h2 className={s.h2}>{title}</h2>
        <Info tip={tip} />
        {pills}
        <div className={s.chAct}>
          {actions}
          <button type="button" className={s.fold} aria-label={`Mở hoặc gập ${title}`} aria-expanded={open} aria-controls={`b-${id}`} onClick={() => setOpen(!open)}>
            {open ? <IconChevronUp size={18} stroke={1.9} aria-hidden="true" /> : <IconChevronDown size={18} stroke={1.9} aria-hidden="true" />}
          </button>
        </div>
      </div>
      {open && <div id={`b-${id}`} className={s.cb}>{children}</div>}
    </section>
  );
}

const ini = (n: string) => n.replace(/[^A-Za-zÀ-ỹ]/g, '').slice(0, 2).toUpperCase() || '—';
const BILLS = ['Original', 'Surrender', 'Seaway'] as const;

type Todo = { k: string; who: string; due: string; done?: boolean; soon?: boolean; act?: string };
type Pic = { role: string; who: string };

function build(rec: BlRec): { todos: Todo[]; info: [string, string, boolean?][]; pics: Pic[]; job: string } {
  if (rec.kind === 'draft') {
    const r = rec.r;
    const t: Todo[] = [];
    if (isBlocked(r)) t.push({ k: `Tạo LOI ${r.type} từ mẫu ${r.car}`, who: 'Chưa giao', due: r.due, soon: true, act: 'Tạo' });
    if (r.resp === 'fix') t.push({ k: 'Sửa Draft theo yêu cầu khách', who: 'Chưa giao', due: r.wh ?? r.due, soon: !!r.whWarn, act: 'Soạn' });
    if (r.resp === 'none' && !isBlocked(r)) t.push({ k: 'Gửi Draft cho khách duyệt', who: 'Chưa giao', due: r.wh ?? r.due, act: 'Gửi' });
    if (r.resp === 'wait') t.push({ k: `Nhắc khách xác nhận Draft v${r.ver}`, who: 'Chưa giao', due: r.wh ?? r.due, soon: !!r.whWarn, act: 'Gửi lại' });
    t.push({ k: 'Khách xác nhận Draft', who: r.cus, due: r.due, done: r.resp === 'ok' });
    return {
      todos: t, job: r.job,
      info: [['Job', r.job, true], ['HBL', r.hbl, true], ['Khách', r.cus], ['Consignee', r.cne], ['Hãng', r.car], ['Loại B/L', r.type], ['Hạn', r.due]],
      pics: [{ role: 'CS lập & phát hành · nhận xử lý', who: 'Chưa giao' }, { role: 'Soát chéo · được giao', who: 'Chưa giao' }],
    };
  }
  if (rec.kind === 'final') {
    const r = rec.r;
    return {
      todos: [{ k: 'Gửi Final B/L cho khách', who: 'Chưa giao', due: r.due, done: r.st === 'sent', soon: r.st !== 'sent', act: r.st === 'sent' ? undefined : 'Gửi' }],
      job: r.job,
      info: [['Job', r.job, true], ['HBL', r.hbl, true], ['Khách', r.cus], ['Tàu / chuyến', r.vsl], ['Hạn', r.due]],
      pics: [{ role: 'CS lập & phát hành · nhận xử lý', who: 'Chưa giao' }],
    };
  }
  if (rec.kind === 'amd') {
    const r = rec.r;
    return {
      todos: [{ k: 'Duyệt phí sửa B/L (mã AMD)', who: r.by === 'Khách' ? r.cus : 'Chưa giao', due: r.st === 'wait' ? 'chờ duyệt' : '', done: r.st !== 'wait' }],
      job: r.job, info: [['Job', r.job, true], ['HBL', r.hbl, true], ['Khách', r.cus], ['Lỗi do', r.by]],
      pics: [{ role: 'CS lập & phát hành · nhận xử lý', who: 'Chưa giao' }],
    };
  }
  if (rec.kind === 'loi') {
    const r = rec.r;
    return { todos: [], job: '', info: [['Hãng', r.car], ['Loại', r.kind], ['Phiên bản', r.ver]], pics: [{ role: 'Chủ mẫu · Trưởng CS', who: 'Chưa giao' }] };
  }
  if (rec.kind === 'orig') {
    const r = rec.r;
    return { todos: [], job: r.job, info: [['Job', r.job, true], ['Số', r.no, true], ['Khách', r.cus], ['Đang ở', r.at]], pics: [{ role: 'CS · nhận xử lý', who: 'Chưa giao' }] };
  }
  if (rec.kind === 'mnf') {
    const r = rec.r;
    return {
      todos: [{ k: `Khai ${r.kind || 'manifest'}`, who: r.by ?? 'Chưa giao', due: r.due, soon: r.st === 'todo', done: r.st !== 'todo' }],
      job: r.job, info: [['Job', r.job, true], ['HBL', r.hbl, true], ['Nơi đến', r.dest], ['Loại khai', r.kind], ['Hạn', r.due]],
      pics: [{ role: 'CS · nhận xử lý', who: r.by ?? 'Chưa giao' }],
    };
  }
  const r = rec.r;
  const t: Todo[] = r.missing.map((m) => ({ k: m, who: r.who[0] ?? 'Chưa giao', due: 'trước release', soon: true }));
  if (r.step === 2 && r.mine) t.push({ k: 'Duyệt hoặc từ chối release', who: r.lvl, due: 'chờ bạn', soon: true, act: 'Duyệt' });
  return {
    todos: t, job: r.job,
    info: [['Job', r.job, true], ['Khách', r.cus], ['Loại B/L', r.type], ['Yêu cầu qua', r.via], ['Điều khoản', r.terms]],
    pics: [{ role: 'CS · nhận xử lý', who: r.who[0] ?? 'Chưa giao' }, { role: 'Sale · theo khách', who: r.who[1] ?? 'Chưa giao' }, { role: 'Duyệt nợ · theo ghế', who: r.lvl }],
  };
}

function Detail({ rec, onBack }: { rec: BlRec; onBack: () => void }) {
  const t = useT();
  const toast = useToast();
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const v = view(rec);
  const b = build(rec);
  const [billT, setBillT] = useState<string>(rec.kind === 'draft' ? rec.r.type : '');
  const [diff, setDiff] = useState(false);
  const [menu, setMenu] = useState(false);
  const copy = (k: string, val: string) => {
    void navigator.clipboard?.writeText(val);
    toast({ msg: `Đã sao chép ${k} · ${val}` });
  };
  const openN = b.todos.filter((x) => !x.done).length;
  const draft: BlDraft | null = rec.kind === 'draft' ? rec.r : null;
  const block = draft ? isBlocked(draft) : false;
  const needLoi = billT === 'Surrender' || billT === 'Telex';
  const left = rec.kind === 'final' ? finalLeft(rec.r, NOW_MIN) : null;
  const chips = [v.kind, rec.kind === 'draft' ? rec.r.type : rec.kind === 'rel' ? rec.r.type : ''].filter(Boolean);
  const primaryOff = v.actDone || !!v.actOff;
  const hbl = rec.kind === 'draft' || rec.kind === 'final' || rec.kind === 'amd' || rec.kind === 'mnf' ? rec.r.hbl : '';
  const close = v.act === 'Đóng';

  return (
    <>
      <PageHeader findPad
        title={v.title}
        tabs={<span className={s.hChips}>{chips.map((c, i) => <span key={c} className={`${s.pill} ${i === 0 ? s.pInfo : ''}`}>{c}</span>)}</span>}
        actions={
          <>
            <button type="button" className={s.back} aria-label="Quay lại danh sách chứng từ" title="Quay lại danh sách chứng từ" onClick={onBack}>
              <IconArrowLeft size={18} aria-hidden="true" />
            </button>
            <button type="button" className={s.more} aria-label="Thêm thao tác" title="Thêm thao tác" aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu(!menu)}>
              <IconDots size={18} aria-hidden="true" />
            </button>
            <Button variant={v.actDone || close ? 'ghost' : 'primary'} disabled={primaryOff && !close} title={v.actOff} onClick={close ? onBack : noWrite}>{v.act}</Button>
          </>
        }
      />
      {menu && (
        <div role="menu" aria-label="Thao tác khác" className={s.card} style={{ position: 'absolute', right: 24, top: 64, zIndex: 20, padding: 6, minWidth: 220 }}>
          {([['Mở trang soạn B/L', !!hbl, noWrite], ['Sao chép số ' + (hbl ? 'HBL' : 'job'), true, () => copy(hbl ? 'HBL' : 'Job', hbl || b.job)], ['Đóng chi tiết', true, onBack]] as const).map(([k, en, go]) => (
            <button key={k} type="button" role="menuitem" aria-disabled={!en} className={s.link} style={{ width: '100%', border: 0, background: 'transparent', font: 'inherit', cursor: en ? 'pointer' : 'default', opacity: en ? 1 : 0.5 }} onClick={() => { setMenu(false); if (en) go(); }}>{k}</button>
          ))}
        </div>
      )}
      <div className={s.page}>
        <div className={s.sub}>{v.sub}</div>
        <div className={s.grid}>
          <div className={s.col}>
            <Card id="sum" title="Tóm tắt" icon={<IconFileText size={18} stroke={1.9} />} tip={TIP.sum} pills={<span className={s.pill}>{v.kind}</span>}>
              <dl className={s.facts}>
                {v.facts.map(([k, val]) => (
                  <div key={k}>
                    <dt>{k}</dt>
                    <dd className={k === 'Khách phản hồi' && val.startsWith('Yêu cầu sửa') ? s.err : undefined}>{val || '—'}</dd>
                  </div>
                ))}
              </dl>
            </Card>

            {draft && (
              <>
                <Card id="type" title="Loại bill của lô này" icon={<IconFileText size={18} stroke={1.9} />} tip={TIP.type} pills={<span className={s.pill}>{`nguồn ${draft.src}`}</span>}>
                  <div className={s.segs} role="radiogroup" aria-label="Loại bill">
                    {BILLS.map((k) => (
                      <button key={k} type="button" role="radio" aria-checked={billT === k} className={`${s.seg} ${billT === k ? s.segOn : ''}`} onClick={() => { setBillT(k); toast({ msg: `Đổi loại bill ${draft.hbl} → ${k} · Draft cũ hết hiệu lực` }); }}>{k}</button>
                    ))}
                  </div>
                </Card>
                <Card id="loi" title="LOI" icon={<IconShieldCheck size={18} stroke={1.9} />} tip={TIP.loi}>
                  <div className={`${s.loi} ${block ? s.loiBad : ''}`}>
                    <span className={s.loiI} aria-hidden="true">{block ? '!' : '✓'}</span>
                    <span className={s.loiTx}>
                      <b>{needLoi ? (block ? `Thiếu LOI ${billT} mẫu ${draft.car}` : `LOI ${billT} · mẫu ${draft.car} đã kèm`) : 'Loại bill này không cần LOI'}</b>
                      <div>{block ? (draft.car === 'MSC' ? 'Hãng MSC chưa có mẫu → dùng mẫu HANA chuẩn, gắn cờ cho Trưởng CS bổ sung' : 'Tự điền dữ liệu job ra PDF, khách ký đóng dấu') : 'Bản ký đóng dấu cần trước khi release'}</div>
                    </span>
                    {block && <Button variant="secondary" size="sm" onClick={noWrite}>{t('bl.loiFromTpl')}</Button>}
                  </div>
                </Card>
                <Card
                  id="vers" title="Phiên bản Draft" icon={<IconHistory size={18} stroke={1.9} />} tip={TIP.ver}
                  actions={<Button variant="secondary" size="sm" disabled={draft.ver < 2} onClick={() => setDiff(!diff)}>{diff ? 'Ẩn so sánh phiên bản' : 'So sánh 2 bản gần nhất'}</Button>}
                >
                  {draft.ver === 0 && <div className={s.foot}>{t('bl.noDraft')}</div>}
                  {Array.from({ length: draft.ver }, (_, i) => i + 1).map((n) => (
                    <div key={n} className={s.ver}>
                      <b>{`v${n}`}</b>
                      <span className={s.verTx}><div>{n === draft.ver ? 'Bản đang hiệu lực' : 'Bản cũ'}</div><div>{n === draft.ver ? draft.sent : `21/Oct/2026 11:0${n}`}</div></span>
                      <span className={`${s.pill} ${n === draft.ver ? s.pInfo : ''}`}>{n === draft.ver ? ({ wait: 'Chờ', fix: 'Yêu cầu sửa', ok: 'Xác nhận', none: 'Nháp' } as const)[draft.resp] : 'Hết hiệu lực'}</span>
                    </div>
                  ))}
                  {diff && (
                    <div className={s.diff}>
                      <b>{t('bl.fieldCol')}</b><b>{`v${Math.max(1, draft.ver - 1)}`}</b><b>{`v${Math.max(1, draft.ver)}`}</b>
                      {([['Consignee', 'Pacific Apparel LLC, 1200 E 7th St', 'Pacific Apparel LLC, 1250 E 7th St, Suite 4'], ['Gross weight', '18.420 kg', '18.240 kg'], ['Mô tả hàng', 'Vải dệt kim 100% cotton', 'Vải dệt kim 100% cotton · HS 6006.22']] as const).map(([k, a, c]) => (
                        <span key={k} style={{ display: 'contents' }}><span>{k}</span><span className={s.a}>{a}</span><span className={s.b}>{c}</span></span>
                      ))}
                    </div>
                  )}
                </Card>
              </>
            )}

            {rec.kind === 'final' && left && (
              <Card id="final" title="Hạn gửi Final B/L" icon={<IconClock size={18} stroke={1.9} />} tip={TIP.fin}>
                <div>
                  <b className={s.cd}>{left.kind === 'sent' ? 'Đã gửi' : left.kind === 'overdue' ? 'Quá hạn' : `${left.hours}h ${String(left.minutes).padStart(2, '0')}p`}</b>
                  <span className={s.cdSub}>{left.kind === 'sent' ? rec.r.sentAt : 'còn lại'}</span>
                </div>
                <div className={s.bar}><div style={{ width: left.kind === 'sent' ? '100%' : left.kind === 'overdue' ? '100%' : `${Math.max(4, Math.min(100, Math.round(((120 - (left.hours * 60 + left.minutes)) / 120) * 100)))}%` }} /></div>
                <div className={s.foot}>{t('bl.fblAutoClose')}</div>
              </Card>
            )}

            {rec.kind === 'loi' && (
              <Notes
                scope="bl"
                id={b.job}
                tip="Luật tự điền của mẫu"
                notes={[{ id: 'tpl', kind: 'Mẫu', by: 'Hệ thống', at: '', text: t('bl.loiAuto') }]}
                onAdd={noWrite}
              />
            )}

            {rec.kind === 'rel' && (
              <Card id="chain" title="Luồng duyệt release" icon={<IconGitBranch size={18} stroke={1.9} />} tip={TIP.chain}>
                {RELEASE_STEPS.map((k, i) => {
                  const r = rec.r;
                  const dn = i < r.step, cur = i === r.step;
                  const who = i === 0 ? `[CS] ${r.who[0] ?? ''}` : i === 1 ? `[Sale] ${r.who[1] ?? ''}` : i === 2 ? r.lvl : i === 3 ? 'LOI bản ký · hãng xác nhận telex MBL' : 'CS gửi mail mẫu + PDF logo';
                  return (
                    <div key={k} className={s.step}>
                      <div className={s.dotCol}>
                        <span className={`${s.dot} ${dn ? s.dotDone : cur ? s.dotCur : ''}`}>{dn ? '✓' : i + 1}</span>
                        {i < RELEASE_STEPS.length - 1 && <span className={`${s.ln} ${dn ? s.lnDone : ''}`} />}
                      </div>
                      <div className={s.stepTx}>{k}{(dn || cur) && <div>{who + (i === 2 && r.self ? ' · Tự duyệt' : '')}</div>}</div>
                    </div>
                  );
                })}
                {rec.r.rej && <div className={s.note} role="note"><b>{t('bl.rejected')}</b><div>{rec.r.rej.why}</div></div>}
                {!rec.r.rej && rec.r.missing.length > 0 && rec.r.step < 5 && (
                  <div className={s.note} role="note"><b>{t('bl.relBlocked')}</b><ul>{rec.r.missing.map((m) => <li key={m}>{m}</li>)}</ul></div>
                )}
              </Card>
            )}
          </div>

          <div className={s.col}>
            <Card id="todo" title="Việc cần làm" icon={<IconChecklist size={18} stroke={1.9} />} tip={TIP.todo} pills={openN > 0 ? <span className={`${s.pill} ${s.pWarn}`}>{openN}</span> : undefined}>
              {b.todos.length === 0 && <div className={s.foot}>{t('bl.noOpenTask')}</div>}
              {b.todos.slice(0, 4).map((t) => (
                <div key={t.k} className={s.todo} role="listitem">
                  <span className={`${s.chk} ${t.done ? s.chkOn : ''}`} aria-label={t.done ? 'Đã xong' : 'Chưa xong'}>{t.done && <IconCheck size={12} stroke={3} aria-hidden="true" />}</span>
                  <span className={s.todoTx}>
                    <span style={{ textDecoration: t.done ? 'line-through' : 'none' }}>{t.k}</span>
                    <span className={s.todoM}><span className={`${s.av} ${s.av18}`} title={t.who}>{ini(t.who)}</span><span>{t.who}</span><span className={t.soon ? s.warnTx : undefined}>{t.due}</span></span>
                  </span>
                  {t.act && !t.done && <Button variant="secondary" size="sm" onClick={noWrite}>{t.act}</Button>}
                </div>
              ))}
            </Card>

            <Card id="info" title="Thông tin chính" icon={<IconListDetails size={18} stroke={1.9} />} tip={TIP.info}>
              {b.info.map(([k, val, cp]) => (
                <div key={k} className={s.kv}>
                  <span className={s.k}>{k}</span>
                  {k === 'Job' ? <Link to="/ezy/jobs/$jobId" params={{ jobId: val }} className={`${s.v} ${s.lnk}`} title={val}>{val} ↗</Link> : <span className={s.v} title={val}>{val}</span>}
                  {cp && <button type="button" className={s.sq} aria-label={`Sao chép ${k}`} title="Sao chép" onClick={() => copy(k, val)}><IconCopy size={15} aria-hidden="true" /></button>}
                </div>
              ))}
            </Card>

            <Card id="pic" title="Người phụ trách" icon={<IconUsers size={18} stroke={1.9} />} tip={TIP.pic}>
              {b.pics.map((p) => (
                <div key={p.role} className={s.pic} role="listitem" title={`${p.role} · ${p.who}`}>
                  <span className={s.av} title={p.who}>{p.who === 'Chưa giao' ? '—' : ini(p.who)}</span>
                  <span className={s.picTx}><span className={p.who === 'Chưa giao' ? s.mute : undefined}>{p.who}</span><div>{p.role}</div></span>
                  {p.who === 'Chưa giao' && <button type="button" className={s.give} aria-label={`Giao ${p.role}`} onClick={noWrite}>Giao</button>}
                </div>
              ))}
            </Card>

            <Card id="links" title="Liên kết" icon={<IconLink size={18} stroke={1.9} />} tip={TIP.links}>
              {b.job && <Link to="/ezy/jobs/$jobId" params={{ jobId: b.job }} className={s.link}><IconLink size={16} aria-hidden="true" /><span>{`Job ${b.job}`}</span><span>{t('bl.jobDetail')}</span></Link>}
              {hbl && <a className={s.link} href="/ezy/bl" title="Trang soạn B/L 2:1" onClick={(e) => { e.preventDefault(); noWrite(); }}><IconNotes size={16} aria-hidden="true" /><span>{`${t('bl.editBl')} ${hbl}`}</span><span>Mint-BLEdit ↗</span></a>}
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}

/** Mint-BL-iPad-Detail · Draft: cột trái LOI + Draft B/L, cột phải Việc · Thông tin chính · Người phụ trách · Liên kết. */
function PadDraftDetail({ r, onBack }: { r: BlDraft; onBack: () => void }) {
  const t = useT();
  const toast = useToast();
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const copy = (k: string, val: string) => {
    void navigator.clipboard?.writeText(val);
    toast({ msg: `Đã sao chép ${k} · ${val}` });
  };
  const x = PAD_DRAFT_X[r.id];
  const block = isBlocked(r);
  const b = build({ kind: 'draft', r });
  const open = b.todos.filter((t) => !t.done);
  const mbl = x?.mbl ?? '—';
  const hl = r.id === 'd1';
  const boxes: [string, string, boolean?][] = [
    ['Shipper', r.cus], ['Consignee', r.cne, hl], ['Notify', 'Same as consignee'], ['Tàu / chuyến', x?.vsl ?? '—'],
    ['POL', x?.pol ?? '—'], ['POD', x?.pod ?? '—'], ['Container / seal', x?.cont ?? '—'], ['Gross weight', x?.gw ?? '—', hl],
  ];
  return (
    <>
      <PageHeader findPad
        title={r.hbl}
        tabs={<span className={s.hChips}><span className={`${s.pill} ${s.pInfo}`}>{`Draft B/L · ${r.car}`}</span></span>}
        actions={
          <>
            <button type="button" className={s.back} aria-label="Quay lại danh sách Draft" title="Quay lại danh sách Draft" onClick={onBack}><IconArrowLeft size={18} aria-hidden="true" /></button>
            <button type="button" className={s.more} aria-label="Thêm thao tác" title="Thêm thao tác" onClick={noWrite}><IconDots size={18} aria-hidden="true" /></button>
            <Button variant="primary" disabled={block || r.resp === 'ok'} onClick={noWrite}>{block ? 'Thiếu LOI' : r.resp === 'ok' ? 'Khách đã xác nhận' : r.resp === 'none' ? 'Gửi Draft' : `Gửi v${r.ver + 1}`}</Button>
          </>
        }
      />
      <div className={s.page}>
        <div className={s.sub}>{`${r.job} · ${r.cus}`}</div>
        <div className={s.grid}>
          <div className={s.col}>
            <Card id="loi" title="LOI" icon={<IconShieldCheck size={18} stroke={1.9} />} tip={TIP.loi}>
              <div className={`${s.loi} ${block ? s.loiBad : ''}`}>
                <span className={s.loiI} aria-hidden="true">{block ? '!' : '✓'}</span>
                <span className={s.loiTx}>
                  <b>{r.type === 'Surrender' || r.type === 'Telex' ? (block ? `Thiếu LOI mẫu ${r.car} · chặn gửi` : `LOI mẫu ${r.car} đã kèm`) : 'Không cần LOI'}</b>
                </span>
                {block && <Button variant="secondary" size="sm" onClick={noWrite}>{t('bl.loiFromTpl')}</Button>}
              </div>
            </Card>
            <Card id="draft" title="Draft B/L" icon={<IconFileText size={18} stroke={1.9} />} tip={TIP.ver}>
              <div className={s.bill}>
                {boxes.map(([k, v, h]) => <div key={k} className={h ? s.billHl : undefined}><span>{k}</span><b>{v}</b></div>)}
              </div>
              <div className={s.billAct}>
                <Button variant="secondary" onClick={noWrite}>{t('bl.viewPrint')}</Button>
                <Button variant="secondary" disabled={r.ver < 2} onClick={noWrite}>{t('bl.compareVer')}</Button>
              </div>
            </Card>
          </div>
          <section aria-label="Tóm tắt bên phải" className={s.col}>
            <Card id="todo" title="Việc cần làm" icon={<IconChecklist size={18} stroke={1.9} />} tip={TIP.todo}>
              {b.todos.slice(0, 4).map((t) => (
                <div key={t.k} className={s.todo} role="listitem">
                  <span className={`${s.chk} ${t.done ? s.chkOn : ''}`} aria-label={t.done ? 'Đã xong' : 'Chưa xong'}>{t.done && <IconCheck size={12} stroke={3} aria-hidden="true" />}</span>
                  <span className={s.todoTx}>
                    <span style={{ textDecoration: t.done ? 'line-through' : 'none' }}>{t.k}</span>
                    <span className={s.todoM}><span>{t.who}</span><span className={t.soon ? s.warnTx : undefined}>{t.due}</span></span>
                  </span>
                  {t === open[0] && t.act && <Button variant="secondary" size="sm" onClick={noWrite}>{t.act === 'Soạn' ? 'Tạo' : t.act}</Button>}
                </div>
              ))}
            </Card>
            <Card id="info" title="Thông tin chính" icon={<IconListDetails size={18} stroke={1.9} />} tip={TIP.info}>
              <div className={s.kv}>
                <span className={s.k}>Job</span>
                <Link to="/ezy/jobs/$jobId" params={{ jobId: r.job }} className={`${s.v} ${s.lnk}`} title={r.job}>{r.job} ↗</Link>
                <button type="button" className={s.sq} aria-label="Sao chép Job" title="Sao chép" onClick={() => copy('Job', r.job)}><IconCopy size={15} aria-hidden="true" /></button>
              </div>
              {([['HBL', r.hbl], ['MBL', mbl]] as const).map(([k, v]) => (
                <div key={k} className={s.kv}>
                  <span className={s.k}>{k}</span><span className={s.v} title={v}>{v}</span>
                  <button type="button" className={s.sq} aria-label={`Sao chép ${k}`} title="Sao chép" onClick={() => copy(k, v)}><IconCopy size={15} aria-hidden="true" /></button>
                </div>
              ))}
              {([['Khách', r.cus], ['Hãng', r.car], ['Loại B/L', r.type], ['Hạn', r.due]] as const).map(([k, v]) => <div key={k} className={s.kv}><span className={s.k}>{k}</span><span className={s.v} title={v}>{v}</span></div>)}
            </Card>
            <Card id="pic" title="Người phụ trách" icon={<IconUsers size={18} stroke={1.9} />} tip={TIP.pic}>
              <div className={s.pic} role="listitem"><span className={s.av}>—</span><span className={s.picTx}><span>{'THUỶ'}</span><div>{t('bl.csIssue')}</div></span></div>
              <div className={s.pic} role="listitem">
                <span className={s.av}>—</span><span className={s.picTx}><span className={s.mute}>{t('bl.unassigned')}</span><div>{t('bl.crossCheck')}</div></span>
                <button type="button" className={s.give} aria-label="Giao Soát chéo" onClick={noWrite}>Giao</button>
              </div>
            </Card>
            <Card id="links" title="Liên kết" icon={<IconLink size={18} stroke={1.9} />} tip={TIP.links}>
              <Link to="/ezy/jobs/$jobId" params={{ jobId: r.job }} className={s.link}><IconLink size={16} aria-hidden="true" /><span>{`Job ${r.job}`}</span><span>{t('bl.jobDetail')}</span></Link>
              <a className={s.link} href="/ezy/bl" onClick={(e) => { e.preventDefault(); noWrite(); }}><IconNotes size={16} aria-hidden="true" /><span>{t('bl.editBl')}</span><span>Mint-BLEdit ↗</span></a>
              <a className={s.link} href="/ezy/booking" onClick={(e) => { e.preventDefault(); noWrite(); }}><IconListDetails size={16} aria-hidden="true" /><span>{t('bl.jobBooking')}</span><span>Booking ↗</span></a>
            </Card>
          </section>
        </div>
      </div>
    </>
  );
}

export function BlDetailPage() {
  const t = useT();
  const layout = useLayout();
  const { id } = useParams({ strict: false }) as { id?: string };
  const navigate = useNavigate();
  const dr = useList('blDraft', { limit: 5000 });
  const fn = useList('blFinal', { limit: 5000 });
  const rl = useList('blRelease', { limit: 5000 });
  const forced = readState();
  const am = useList('blAmend', { limit: 5000 });
  const lo = useList('blLoi', { limit: 5000 });
  const og = useList('blOriginal', { limit: 5000 });
  const mf = useList('blManifest', { limit: 5000 });
  const d = dr.data?.items.find((x) => x.id === id), f = fn.data?.items.find((x) => x.id === id), r = rl.data?.items.find((x) => x.id === id);
  const a = am.data?.items.find((x) => x.id === id), l = lo.data?.items.find((x) => x.id === id), o = og.data?.items.find((x) => x.id === id), m = mf.data?.items.find((x) => x.id === id);
  const rec: BlRec | null = d ? { kind: 'draft', r: d } : f ? { kind: 'final', r: f } : r ? { kind: 'rel', r } : a ? { kind: 'amd', r: a } : l ? { kind: 'loi', r: l } : o ? { kind: 'orig', r: o } : m ? { kind: 'mnf', r: m } : null;
  const loading = dr.isLoading || fn.isLoading || rl.isLoading || am.isLoading || lo.isLoading || og.isLoading || mf.isLoading;
  const err = dr.error ?? fn.error ?? rl.error ?? am.error ?? lo.error ?? og.error ?? mf.error;

  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (loading) state = 'loading';
  else if (err) state = err instanceof DataError && err.code === 'noperm' ? 'noperm' : 'error';
  else if (!rec) state = 'empty';

  const back = () => void navigate({ to: '/ezy/bl' });
  if (state === 'ready' && rec) return <Page>{layout === 'pad' && rec.kind === 'draft' ? <PadDraftDetail r={rec.r} onBack={back} /> : <Detail rec={rec} onBack={back} />}</Page>;
  return (
    <Page>
      <PageHeader title={id ?? 'Chi tiết B/L'} actions={<Button variant="secondary" icon={<IconArrowLeft size={16} />} onClick={back}>{t('bl.backToList')}</Button>} />
      <div className={s.page}>
        <StateView
          state={state}
          icon={<IconFileText size={26} stroke={1.8} />}
          title={state === 'empty' ? `Không tìm thấy chứng từ ${id ?? ''}` : undefined}
          action={state === 'error' ? <Button variant="secondary" onClick={() => { void dr.refetch(); void fn.refetch(); void rl.refetch(); void am.refetch(); void lo.refetch(); void og.refetch(); void mf.refetch(); }}>{t('bl.retry')}</Button> : state === 'empty' ? <Button onClick={back}>{t('bl.backToList')}</Button> : undefined}
          skeleton={<div className={s.grid}><div className={s.skel} style={{ height: 420 }}><Skeleton /></div><div className={s.skel} style={{ height: 300 }}><Skeleton /></div></div>}
        >
          {null}
        </StateView>
      </div>
    </Page>
  );
}
